import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/layout/AppShell';
import { LoadingSpinner } from '../components/layout/SharedComponents';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api, { getApiErrorMessage } from '../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { formatTimestamp as fmtTs } from './Requisitions/constants';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';
import {
  ChevronLeft, Check, X, Send, DollarSign, FileText, Activity,
  CheckCircle, XCircle, AlertTriangle, Printer
} from 'lucide-react';

const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700', MATCHED: 'bg-green-100 text-green-700',
  VARIANCE: 'bg-amber-100 text-amber-700', PENDING_APPROVAL: 'bg-blue-100 text-blue-700',
  APPROVED: 'bg-emerald-100 text-emerald-700', REJECTED: 'bg-red-100 text-red-700',
  PARTIALLY_PAID: 'bg-cyan-100 text-cyan-700', PAID: 'bg-green-200 text-green-800',
};

export default function InvoiceDetailPage() {
  const { invoiceRef } = useParams();
  const navigate = useNavigate();
  const { user, systemSettings } = useAuth();
  const currency = systemSettings?.system_currency || 'USD';
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const fmtTz = (d) => fmtTs(d, tz);

  const [inv, setInv] = useState(null);
  const [invLineSort, handleInvLineSort] = useSortState('', 'asc');
  const [invPaySort, handleInvPaySort] = useSortState('payment_date', 'desc');
  const [invEvtSort, handleInvEvtSort] = useSortState('event_date', 'desc');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [showPayment, setShowPayment] = useState(false);
  const [paymentData, setPaymentData] = useState({ amount: '', payment_method: '', payment_ref: '', payment_date: new Date().toISOString().split('T')[0] });
  const [processing, setProcessing] = useState(false);

  const load = useCallback(() => {
    api.get(`/api/invoices/${invoiceRef}`).then(r => setInv(r.data)).catch(() => toast.error('Failed to load invoice')).finally(() => setLoading(false));
  }, [invoiceRef]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <AppShell><LoadingSpinner /></AppShell>;
  if (!inv) return <AppShell><p>Invoice not found</p></AppShell>;

  const canEdit = ['DRAFT', 'VARIANCE'].includes(inv.status);
  const canMatch = inv.order_number && ['DRAFT', 'VARIANCE'].includes(inv.status);
  const canSubmit = ['DRAFT', 'MATCHED', 'VARIANCE'].includes(inv.status);
  const canApprove = inv.status === 'PENDING_APPROVAL' && inv.approval_chain?.some(a => a.status === 'WAITING' && a.user_id === user?.user_id);
  const canPay = ['APPROVED', 'PARTIALLY_PAID'].includes(inv.status);
  const balance = (inv.invoice_gross_total || 0) - (inv.amount_paid || 0);

  const runMatch = async () => {
    setProcessing(true);
    try {
      const r = await api.post(`/api/invoices/${invoiceRef}/match`);
      toast.success(`Match: ${r.data.match_status}. Tolerance: ${r.data.tolerance_pct}%`);
      if (r.data.variances?.length > 0) r.data.variances.forEach(v => toast.warning(v));
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Match failed')); }
    setProcessing(false);
  };

  const submitForApproval = async () => {
    setProcessing(true);
    try {
      const r = await api.post(`/api/invoices/${invoiceRef}/submit`, {});
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Submit failed')); }
    setProcessing(false);
  };

  const approveInvoice = async () => {
    setProcessing(true);
    try {
      const r = await api.post(`/api/invoices/${invoiceRef}/approve`, { note: '' });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Approve failed')); }
    setProcessing(false);
  };

  const rejectInvoice = async () => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    try {
      const r = await api.post(`/api/invoices/${invoiceRef}/reject`, { reason });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Reject failed')); }
  };

  const recordPayment = async () => {
    const amt = parseFloat(paymentData.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setProcessing(true);
    try {
      const r = await api.post(`/api/invoices/${invoiceRef}/payment`, paymentData);
      toast.success(r.data.message);
      setShowPayment(false);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Payment failed')); }
    setProcessing(false);
  };

  const fmtCurr = (v) => `${currency} ${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  const TABS = [
    { id: 'details', label: 'Details', icon: FileText },
    { id: 'matching', label: 'PO Matching', icon: CheckCircle },
    { id: 'approvals', label: 'Approvals', icon: Check },
    { id: 'payments', label: 'Payments', icon: DollarSign },
    { id: 'events', label: 'Audit Trail', icon: Activity },
  ];

  return (
    <AppShell>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={() => navigate('/invoices')}><ChevronLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="text-lg font-bold font-mono">{inv.invoice_ref}</h1>
            <p className="text-xs text-muted-foreground">Supplier Invoice: {inv.supplier_invoice_number} | {inv.supplier_name}</p>
          </div>
          <Badge className={`${STATUS_COLORS[inv.status] || ''} ml-2`}>{(inv.status || '').replace(/_/g, ' ')}</Badge>
          {inv.ghost_po && <Badge className="bg-purple-100 text-purple-700 text-[9px]">Ghost PO</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {canMatch && <Button size="sm" variant="outline" onClick={runMatch} disabled={processing}><CheckCircle className="h-4 w-4 mr-1" /> Run Match</Button>}
          {canSubmit && <Button size="sm" onClick={submitForApproval} disabled={processing}><Send className="h-4 w-4 mr-1" /> Submit for Approval</Button>}
          {canApprove && <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={approveInvoice} disabled={processing}><Check className="h-4 w-4 mr-1" /> Approve</Button>}
          {canApprove && <Button size="sm" variant="destructive" onClick={rejectInvoice}><XCircle className="h-4 w-4 mr-1" /> Reject</Button>}
          {canPay && <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => { setPaymentData({ amount: balance.toFixed(2), payment_method: '', payment_ref: '', payment_date: new Date().toISOString().split('T')[0] }); setShowPayment(true); }}><DollarSign className="h-4 w-4 mr-1" /> Record Payment</Button>}
        </div>
      </div>

      {/* Status strip */}
      <div className="grid grid-cols-6 gap-3 mb-4 text-sm">
        <div><p className="text-[10px] text-muted-foreground">Invoice Date</p><p className="font-semibold">{inv.invoice_date || '—'}</p></div>
        <div><p className="text-[10px] text-muted-foreground">Due Date</p><p className="font-semibold">{inv.due_date || '—'}</p></div>
        <div><p className="text-[10px] text-muted-foreground">PO Number</p><p className="font-semibold font-mono">{inv.order_number || 'No PO'}</p></div>
        <div><p className="text-[10px] text-muted-foreground">Gross Total</p><p className="font-semibold">{fmtCurr(inv.invoice_gross_total)}</p></div>
        <div><p className="text-[10px] text-muted-foreground">Amount Paid</p><p className="font-semibold text-green-700">{fmtCurr(inv.amount_paid)}</p></div>
        <div><p className="text-[10px] text-muted-foreground">Balance Due</p><p className={`font-semibold ${balance > 0 ? 'text-red-600' : 'text-green-700'}`}>{fmtCurr(balance)}</p></div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b mb-4">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <t.icon className="h-3.5 w-3.5 inline mr-1.5" />{t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'details' && (
        <div>
          <h3 className="font-bold text-sm mb-3">Line Items</h3>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-muted/50 border-b">
              <SortableTableHeader label="#" field="item_num" sortState={invLineSort} onSort={handleInvLineSort} />
              <SortableTableHeader label="Item Code" field="item_code" sortState={invLineSort} onSort={handleInvLineSort} />
              <SortableTableHeader label="Description" field="item_description" sortState={invLineSort} onSort={handleInvLineSort} />
              <SortableTableHeader label="Qty" field="invoice_quantity" sortState={invLineSort} onSort={handleInvLineSort} className="text-right" />
              <SortableTableHeader label={`Unit Price (${currency})`} field="invoice_unit_price" sortState={invLineSort} onSort={handleInvLineSort} className="text-right" />
              <SortableTableHeader label="Net" field="invoice_net_price" sortState={invLineSort} onSort={handleInvLineSort} className="text-right" />
              <SortableTableHeader label="Match" field="match_status" sortState={invLineSort} onSort={handleInvLineSort} />
            </tr></thead>
            <tbody>
              {sortData((inv.line_items || []), invLineSort.field, invLineSort.dir).map((li, i) => (
                <tr key={li.id || i} className="border-b hover:bg-accent/20">
                  <td className="px-3 py-1.5">{li.item_num || i + 1}</td>
                  <td className="px-3 py-1.5">{li.item_code || '—'}</td>
                  <td className="px-3 py-1.5 max-w-[200px] truncate">{li.item_description || '—'}</td>
                  <td className="px-3 py-1.5 text-right">{li.invoice_quantity}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{(li.invoice_unit_price || 0).toFixed(2)}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-semibold">{(li.invoice_net_price || 0).toFixed(2)}</td>
                  <td className="px-3 py-1.5">
                    <Badge className={`text-[9px] ${li.match_status === 'MATCHED' ? 'bg-green-100 text-green-700' : li.match_status === 'VARIANCE' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                      {li.match_status || 'PENDING'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="border-t-2 font-semibold">
              <td colSpan={5} className="px-3 py-2 text-right">Totals:</td>
              <td className="px-3 py-2 text-right font-mono">{(inv.invoice_net_total || 0).toFixed(2)}</td>
              <td></td>
            </tr></tfoot>
          </table>
          {inv.notes && <p className="text-xs text-muted-foreground mt-3 border-t pt-2">Notes: {inv.notes}</p>}
        </div>
      )}

      {activeTab === 'matching' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <h3 className="font-bold text-sm">PO-to-Invoice Matching</h3>
            <Badge className={`${inv.match_status === 'MATCHED' ? 'bg-green-100 text-green-700' : inv.match_status === 'VARIANCE' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
              {inv.match_status || 'Not run'}
            </Badge>
            {canMatch && <Button size="sm" variant="outline" onClick={runMatch} disabled={processing}>Re-run Match</Button>}
          </div>

          {inv.order_number ? (
            <div className="grid grid-cols-2 gap-4">
              {/* PO Side */}
              <div className="border rounded-lg p-3">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Purchase Order: {inv.order_number}</h4>
                <table className="w-full text-[11px]">
                  <thead><tr className="border-b"><th className="text-left py-1">#</th><th className="text-left py-1">Description</th><th className="text-right py-1">Qty</th><th className="text-right py-1">Price</th><th className="text-right py-1">Net</th></tr></thead>
                  <tbody>
                    {(inv.po_data?.line_items || []).map((li, i) => (
                      <tr key={i} className="border-b"><td className="py-1">{li.item_num}</td><td className="py-1 truncate max-w-[120px]">{li.item_description}</td><td className="py-1 text-right">{li.quantity}</td><td className="py-1 text-right">{(li.unit_price || 0).toFixed(2)}</td><td className="py-1 text-right font-semibold">{(li.net_price || 0).toFixed(2)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Invoice Side */}
              <div className="border rounded-lg p-3">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Invoice: {inv.supplier_invoice_number}</h4>
                <table className="w-full text-[11px]">
                  <thead><tr className="border-b"><th className="text-left py-1">#</th><th className="text-left py-1">Description</th><th className="text-right py-1">Qty</th><th className="text-right py-1">Price</th><th className="text-right py-1">Net</th><th className="py-1">Var</th></tr></thead>
                  <tbody>
                    {(inv.line_items || []).map((li, i) => (
                      <tr key={i} className={`border-b ${li.match_status === 'VARIANCE' ? 'bg-amber-50' : li.match_status === 'MATCHED' ? 'bg-green-50' : ''}`}>
                        <td className="py-1">{li.item_num}</td><td className="py-1 truncate max-w-[120px]">{li.item_description}</td>
                        <td className={`py-1 text-right ${li.qty_variance_pct > 0 ? 'text-amber-700 font-semibold' : ''}`}>{li.invoice_quantity}</td>
                        <td className={`py-1 text-right ${li.price_variance_pct > 0 ? 'text-amber-700 font-semibold' : ''}`}>{(li.invoice_unit_price || 0).toFixed(2)}</td>
                        <td className="py-1 text-right font-semibold">{(li.invoice_net_price || 0).toFixed(2)}</td>
                        <td className="py-1">
                          {li.match_status === 'VARIANCE' && <AlertTriangle className="h-3 w-3 text-amber-600" />}
                          {li.match_status === 'MATCHED' && <CheckCircle className="h-3 w-3 text-green-600" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">No purchase order linked. {inv.ghost_po ? 'This invoice has a ghost PO.' : 'Link a PO to run matching.'}</p>
          )}
        </div>
      )}

      {activeTab === 'approvals' && (
        <div>
          <h3 className="font-bold text-sm mb-3">Approval Chain</h3>
          {(inv.approval_chain || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">{inv.status === 'APPROVED' ? 'Invoice was auto-approved.' : 'No approval chain. Submit the invoice to start the approval process.'}</p>
          ) : (
            <div className="space-y-2">
              {inv.approval_chain.map((a, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${a.status === 'APPROVED' ? 'bg-green-50 border-green-200' : a.status === 'WAITING' ? 'bg-amber-50 border-amber-200' : 'bg-gray-50'}`}>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold bg-white border">{i + 1}</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{a.name || a.user_id}</p>
                    <p className="text-[10px] text-muted-foreground">{a.rule_name || ''}</p>
                  </div>
                  <Badge className={`text-[9px] ${a.status === 'APPROVED' ? 'bg-green-100 text-green-700' : a.status === 'WAITING' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100'}`}>{a.status}</Badge>
                  {a.approved_at && <span className="text-[10px] text-muted-foreground">{fmtTz(a.approved_at)}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'payments' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <h3 className="font-bold text-sm">Payments</h3>
            {canPay && <Button size="sm" onClick={() => { setPaymentData({ amount: balance.toFixed(2), payment_method: '', payment_ref: '', payment_date: new Date().toISOString().split('T')[0] }); setShowPayment(true); }}><DollarSign className="h-3.5 w-3.5 mr-1" /> Record Payment</Button>}
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="border rounded-lg p-3"><p className="text-[10px] text-muted-foreground">Invoice Total</p><p className="text-lg font-bold">{fmtCurr(inv.invoice_gross_total)}</p></div>
            <div className="border rounded-lg p-3"><p className="text-[10px] text-muted-foreground">Total Paid</p><p className="text-lg font-bold text-green-700">{fmtCurr(inv.amount_paid)}</p></div>
            <div className="border rounded-lg p-3"><p className="text-[10px] text-muted-foreground">Balance Due</p><p className={`text-lg font-bold ${balance > 0 ? 'text-red-600' : 'text-green-700'}`}>{fmtCurr(balance)}</p></div>
          </div>
          {(inv.payments || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No payments recorded yet.</p>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-muted/50 border-b">
                <SortableTableHeader label="Date" field="payment_date" sortState={invPaySort} onSort={handleInvPaySort} />
                <SortableTableHeader label="Method" field="payment_method" sortState={invPaySort} onSort={handleInvPaySort} />
                <SortableTableHeader label="Reference" field="payment_ref" sortState={invPaySort} onSort={handleInvPaySort} />
                <SortableTableHeader label={`Amount (${currency})`} field="amount" sortState={invPaySort} onSort={handleInvPaySort} className="text-right" />
                <SortableTableHeader label="By" field="recorded_by" sortState={invPaySort} onSort={handleInvPaySort} />
              </tr></thead>
              <tbody>
                {sortData(inv.payments, invPaySort.field, invPaySort.dir).map(p => (
                  <tr key={p.id} className="border-b">
                    <td className="px-3 py-1.5">{p.payment_date}</td><td className="px-3 py-1.5">{p.payment_method || '—'}</td>
                    <td className="px-3 py-1.5">{p.payment_ref || '—'}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtCurr(p.amount)}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{p.recorded_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'events' && (
        <div>
          <h3 className="font-bold text-sm mb-3">Audit Trail</h3>
          <table className="w-full text-xs">
            <thead><tr className="bg-muted/50 border-b">
              <SortableTableHeader label="Date & Time" field="event_date" sortState={invEvtSort} onSort={handleInvEvtSort} />
              <SortableTableHeader label="User" field="user" sortState={invEvtSort} onSort={handleInvEvtSort} />
              <SortableTableHeader label="Event" field="event" sortState={invEvtSort} onSort={handleInvEvtSort} />
              <SortableTableHeader label="IP" field="ip_address" sortState={invEvtSort} onSort={handleInvEvtSort} className="text-right" />
            </tr></thead>
            <tbody>
              {sortData((inv.events || []), invEvtSort.field, invEvtSort.dir).map((e, i) => (
                <tr key={i} className="border-b hover:bg-accent/20">
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap font-mono">{fmtTz(e.event_date)}</td>
                  <td className="px-3 py-2">{e.user}</td>
                  <td className="px-3 py-2">{e.event}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground font-mono">{e.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment Dialog */}
      {showPayment && (
        <Dialog open onOpenChange={() => setShowPayment(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Amount ({currency})</Label>
                <Input type="number" step="0.01" value={paymentData.amount} onChange={e => setPaymentData(s => ({ ...s, amount: e.target.value }))} className="h-8 text-sm" />
                <p className="text-[10px] text-muted-foreground">Balance due: {fmtCurr(balance)}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Payment Method</Label>
                <select value={paymentData.payment_method} onChange={e => setPaymentData(s => ({ ...s, payment_method: e.target.value }))} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
                  <option value="">Select...</option>
                  <option>Bank Transfer</option><option>Check</option><option>ACH</option><option>Wire</option><option>Credit Card</option><option>Cash</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Payment Reference</Label>
                <Input value={paymentData.payment_ref} onChange={e => setPaymentData(s => ({ ...s, payment_ref: e.target.value }))} className="h-8 text-sm" placeholder="Check #, transaction ID..." />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Payment Date</Label>
                <Input type="date" value={paymentData.payment_date} onChange={e => setPaymentData(s => ({ ...s, payment_date: e.target.value }))} className="h-8 text-sm" />
              </div>
            </div>
            <div className="flex justify-start gap-2 mt-4">
              <Button onClick={recordPayment} disabled={processing}>{processing ? 'Recording...' : 'Record Payment'}</Button>
              <Button variant="outline" onClick={() => setShowPayment(false)}>Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </AppShell>
  );
}
